import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  schemaData?: Record<string, unknown>;
  breadcrumbList?: Array<{ name: string; url: string }>;
  articleData?: {
    headline: string;
    datePublished: string;
    dateModified: string;
    authorName: string;
    image: string;
  };
  faqData?: Array<{ question: string; answer: string }>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = 'website',
  ogImage,
  schemaData,
  breadcrumbList,
  articleData,
  faqData,
}: SEOHeadProps) {
  const baseUrl = 'https://purifypath.github.io';

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setLink('canonical', canonicalUrl);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', canonicalUrl, true);
    if (ogImage) {
      setMeta('og:image', ogImage, true);
    }

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) {
      setMeta('twitter:image', ogImage);
    }

    // Schema.org JSON-LD
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach((s) => s.remove());

    const schemas: Record<string, unknown>[] = [];

    // Organization
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '戒色完全指南',
      url: baseUrl,
      description: '科学系统的戒色方法、心理调节技巧与实用工具，帮助有戒色需求的人群克服成瘾、重建自律。',
    });

    // WebSite
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '戒色完全指南',
      url: baseUrl,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${baseUrl}/search?q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    });

    // WebPage
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: canonicalUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: '戒色完全指南',
        url: baseUrl,
      },
    });

    // BreadcrumbList
    if (breadcrumbList && breadcrumbList.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbList.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      });
    }

    // Article
    if (articleData) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: articleData.headline,
        datePublished: articleData.datePublished,
        dateModified: articleData.dateModified,
        author: {
          '@type': 'Person',
          name: articleData.authorName,
        },
        image: articleData.image,
        publisher: {
          '@type': 'Organization',
          name: '戒色完全指南',
        },
      });
    }

    // FAQPage
    if (faqData && faqData.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqData.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      });
    }

    // Custom schema
    if (schemaData) {
      schemas.push(schemaData);
    }

    schemas.forEach((schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });
  }, [title, description, keywords, canonicalUrl, ogType, ogImage, schemaData, breadcrumbList, articleData, faqData]);

  return null;
}